/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import CloseIcon from '@mui/icons-material/Close'

import { AddComment, Comment } from '..'
import { IconButton } from '../../../../shared/buttons'
import { alternateGray, secondaryGrayColor } from '../../../../../lib/styles/colors'
import { BoardGroupFragment, Comment as CommentType } from '../../../../../generated/graphql'
import { useReadOnly } from '../../../../../lib/hooks/useReadOnly'

const commentCardCss = css`
  position: relative;
  margin-top: 25px;

  background-color: #fff;
  padding: 5px 0;
  border: 1px solid ${secondaryGrayColor};
  border-radius: 4px;
  box-shadow: 0 3px 2px -2px ${secondaryGrayColor};

  &:before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    bottom: 99.5%;
    left: 2.75em;
    border: 0.75rem solid transparent;
    border-top: none;

    border-bottom-color: #fff;
    filter: drop-shadow(0 -0.0625rem 0rem ${secondaryGrayColor});
  }
`
const exitButtonCss = css`
  float: right;
`
const readOnlyExitButtonCss = css`
  position: absolute;
  right: 0;
`
const commentsContainerCss = css`
  max-height: 350px;
  overflow-y: auto;
  padding-bottom: 10px;
`
const emptyCss = css`
  display: flex;
  width: 100%;
`
const noDividerCss = css`
  border: 0;
  margin-top: 10px;
  padding: 0;
`

type CommentCardProps = {
  /** Array of comments to display */
  comments: CommentType[]
  /** Current Plan Group */
  group: BoardGroupFragment
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** Max length of the comment */
  maxLength?: number
  /** Event callback when the cancel or close buttons are clicked */
  onCancel: () => void
  /** Event callback when the save comment button is clicked */
  onSaveComment?: () => void
  /** Indicates if this plan/template is read-only template */
  readOnly?: boolean | undefined
  /** Show the add comment field when clicking Add Comment from the group menu */
  showAddComment: boolean
  /** Show the comments when clicking on the comment count button */
  showComments: boolean
  /** Hide the new comment indicator after the comments have been viewed */
  showNewCommentIndicator: boolean
}

const defaultProps: Pick<CommentCardProps, 'maxLength' | 'onSaveComment' | 'readOnly'> = {
  maxLength: 200,
  onSaveComment: undefined,
  readOnly: false
}

const CommentCard = ({
  comments,
  group,
  isTemplate,
  maxLength,
  onCancel,
  onSaveComment,
  readOnly,
  showAddComment,
  showComments,
  showNewCommentIndicator
}: CommentCardProps) => {
  const { isReadOnly } = useReadOnly()
  const renderComments = () => {
    if (comments && comments.length > 0) {
      return comments.map((cmt, i) => (
        <Comment
          key={cmt.id}
          comment={cmt}
          css={readOnly && i === 0 && noDividerCss}
          isTemplate={isTemplate}
          readOnly={readOnly}
          showNewCommentIndicator={showNewCommentIndicator}
        />
      ))
    }
    return null
  }

  return (
    <div css={commentCardCss}>
      <div>
        <div css={[exitButtonCss, readOnly && readOnlyExitButtonCss]}>
          <IconButton ariaLabel="Close" onClick={onCancel} displayTooltip={false}>
            <CloseIcon fontSize="small" style={{ color: alternateGray }} />
          </IconButton>
        </div>
      </div>
      {(!readOnly || isReadOnly) && (
        <AddComment
          group={group}
          isTemplate={isTemplate}
          maxLength={maxLength}
          onCancel={onCancel}
          onSaveComment={onSaveComment}
          showAddComment={showAddComment}
          showComments={showComments}
        />
      )}
      {/* empty div to align comments when AddComment is hidden */}
      {readOnly && <div css={emptyCss} />}
      <div css={commentsContainerCss}>{renderComments()}</div>
    </div>
  )
}

CommentCard.defaultProps = defaultProps

export default CommentCard
